// Notificações locais via Web Notifications API (SEM Web Push).
// Não exige VAPID, service worker nem servidor — só uma permissão única do
// browser. Funciona enquanto a página estiver aberta (mesmo com a aba em
// segundo plano). Não cobre browser totalmente fechado (isso exigiria push).

export type NotificationPermissionState = "default" | "granted" | "denied" | "unsupported";

export function notificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function getNotificationPermission(): NotificationPermissionState {
  if (!notificationsSupported()) return "unsupported";
  return Notification.permission as NotificationPermissionState;
}

/** Dispara o prompt nativo do browser. Só chamar após priming contextual. */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!notificationsSupported()) return "unsupported";
  try {
    const result = await Notification.requestPermission();
    return result as NotificationPermissionState;
  } catch {
    // Safari antigo usa callback; fallback defensivo
    return Notification.permission as NotificationPermissionState;
  }
}

interface NotifyOptions {
  title: string;
  body?: string;
  /** Agrupa/colapsa notificações da mesma conversa */
  tag?: string;
  /** Chamado quando o usuário clica na notificação (foca a aba + navega) */
  onClick?: () => void;
  /** Se true, dispara mesmo com a aba visível (default: só quando oculta) */
  whenVisible?: boolean;
}

/**
 * Mostra uma notificação do SO. Por padrão só dispara quando a aba NÃO está
 * visível (não faz sentido notificar quem já está olhando). Retorna a
 * Notification criada, ou null se não disparou.
 */
export function notify(opts: NotifyOptions): Notification | null {
  if (!notificationsSupported()) return null;
  if (getNotificationPermission() !== "granted") return null;
  const visible = typeof document !== "undefined" && document.visibilityState === "visible";
  if (visible && !opts.whenVisible) return null;

  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      tag: opts.tag,
    });
    if (opts.onClick) {
      n.onclick = () => {
        try {
          window.focus();
          opts.onClick?.();
        } finally {
          n.close();
        }
      };
    }
    return n;
  } catch {
    return null;
  }
}
